import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';

const ConfirmationOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 20px;
`;

const ConfirmationCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 100%;
  text-align: center;
  animation: slideUp 0.3s ease;
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const Icon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  margin: 0 0 12px 0;
  font-size: 20px;
  color: #212529;
`;

const Message = styled.p`
  color: #6c757d;
  line-height: 1.5;
  margin-bottom: 24px;
`;

const CreditsInfo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 24px;
`;

const CreditsBefore = styled.div`
  text-align: center;
  
  .label {
    font-size: 12px;
    color: #6c757d;
    display: block;
    margin-bottom: 4px;
  }
  
  .amount {
    font-size: 24px;
    font-weight: 700;
    color: #212529;
  }
`;

const Arrow = styled.div`
  color: #6c757d;
  font-size: 20px;
`;

const CreditsAfter = styled.div`
  text-align: center;
  
  .label {
    font-size: 12px;
    color: #6c757d;
    display: block;
    margin-bottom: 4px;
  }
  
  .amount {
    font-size: 24px;
    font-weight: 700;
    color: #4263eb;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const AttentionMessage = styled.div`
  background-color: #fff4e5;
  border-left: 4px solid #ff9800;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-radius: 4px;
  text-align: left;
  font-size: 14px;
  color: #663c00;
`;

const WarningMessage = styled.div<{ $isLoading: boolean }>`
  background-color: #fff4e5;
  border-left: 4px solid #ff2600ff;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-radius: 4px;
  text-align: left;
  font-size: 14px;
  color: #663c00;
  display: ${props => props.$isLoading ? 'block' : 'none'};
`;

interface CreditConfirmationProps {
  isOpen: boolean;
  currentCredits: number;
  onConfirm: () => void;
  onCancel: () => void;
  selectedLanguage?: string;
  error?: string | null;
  isRetry?: boolean; // Add this to indicate if we're in retry mode
  isLoading?: boolean;
  creditCost: number;
}

export const CreditConfirmation: React.FC<CreditConfirmationProps> = ({
  isOpen,
  currentCredits,
  onConfirm,
  onCancel,
  selectedLanguage = 'en',
  error,
  isRetry = false,
  isLoading = false,
  creditCost
}) => {
  const { t, i18n } = useTranslation();

  const getLanguageDisplayName = (langCode: string) => {
    switch(langCode) {
      case 'en': return 'English';
      case 'de': return 'Deutsch';
      case 'ro': return 'Română';
      case 'fr': return 'Français';
      case 'it': return 'Italiano';
      case 'es': return 'Español';
      default: return langCode;
    }
  };

  const language = selectedLanguage || i18n.language;
  const languageName = getLanguageDisplayName(language);
  
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };
  
  return (
    <ConfirmationOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ConfirmationCard onClick={(e) => e.stopPropagation()}>
        <Icon>{error ? '⚠️' : '✨'}</Icon>
        <Title>
          {error 
            ? t('story.generateErrorTitle', 'Generation Failed') 
            : t('story.generateConfirmTitle', 'Generate New Story')}
        </Title>

        {error ? (
          <Message>
            {t('story.generateErrorMessage', 'Story generation failed, but no credits were deducted. Please try again.')}
            <br/>
            {/* <small>{error}</small> */}
          </Message>
        ) : (
          <Message>
            {/* TODO: check why pluralization doesn't work */}
            {/* {t('story.generateConfirmMessage', { count: Number(creditCost) })} */}
          </Message>
        )}
        
        <AttentionMessage>
          <strong>{t('common.attention', 'Attention!')} </strong>
          {t('story.languageNotice', 'The story will be generated in')} <strong>{languageName}</strong>
        </AttentionMessage>

        <WarningMessage $isLoading={isLoading}>
          <strong>{t('common.important', 'Important!')} </strong>
          {t('story.stayOnPageWarning', 'Please stay on this page while your story is being generated.')}
        </WarningMessage>
        
        <CreditsInfo>
          <CreditsBefore>
            <span className="label">{t('credits.before', 'Before')}</span>
            <span className="amount">{currentCredits}</span>
          </CreditsBefore>
          <Arrow>→</Arrow>
          <CreditsAfter>
            <span className="label">{t('credits.after', 'After')}</span>
            <span className="amount">{currentCredits - creditCost}</span>
          </CreditsAfter>
        </CreditsInfo>
        
        <Actions>
          <Button
            variant="outline"
            size="medium"
            onClick={onCancel}
            disabled={isLoading} 
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={onConfirm}
            isLoading={isLoading}
            disabled={isLoading} 
          >
            {isLoading 
              ? t('story.generating', 'Generating...') 
              : error 
                ? t('story.retryGenerate', 'Try Again') 
                : t('story.confirmGenerate', 'Generate Story')}
          </Button>
        </Actions>
      </ConfirmationCard>
    </ConfirmationOverlay>
  );
};